import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { seoService } from '../../services/api';
import { useData } from '../../contexts/DataContext';

/**
 * Get pre-loaded SEO data from Django template
 * This data is embedded in the HTML before React loads
 */
const getPreloadedSEO = () => {
  const seoElement = document.getElementById('seo-data');
  if (seoElement) {
    try {
      const data = JSON.parse(seoElement.textContent);
      console.log('✅ Preloaded SEO data found:', data);
      return data;
    } catch (e) {
      console.error('❌ Error parsing preloaded SEO:', e);
    }
  }
  console.log('⚠️ No preloaded SEO data found');
  return null;
};

const SEO = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonical,
  robots,
  children,
}) => {
  const { data } = useData();
  const siteConfig = data.siteConfig;
  const [preloadedSEO] = useState(() => getPreloadedSEO());

  // Remove the preloaded SEO script after reading (cleanup)
  useEffect(() => {
    const seoElement = document.getElementById('seo-data');
    if (seoElement) {
      seoElement.remove();
      console.log('🗑️ Preloaded SEO script removed from DOM');
    }
  }, []);

  // Merge SEO data with priority: props > preloaded > siteConfig > defaults
  const seoData = {
    meta_title: 
      title || 
      preloadedSEO?.meta_title || 
      siteConfig?.default_meta_title || 
      'Resumeslip - Build Professional Resumes & Slips',
    
    meta_description: 
      description || 
      preloadedSEO?.meta_description || 
      siteConfig?.default_meta_description || 
      'Create professional resumes and slips for free. Easy to use, customizable templates.',
    
    meta_keywords: 
      keywords || 
      preloadedSEO?.meta_keywords || 
      'resume, cv, slip, professional, builder, free',
    
    og_title: 
      ogTitle || 
      title || 
      preloadedSEO?.og_title || 
      preloadedSEO?.meta_title || 
      siteConfig?.default_meta_title || 
      'Resumeslip',
    
    og_description: 
      ogDescription || 
      description || 
      preloadedSEO?.og_description || 
      preloadedSEO?.meta_description || 
      siteConfig?.default_meta_description || 
      'Build professional resumes',
    
    og_image: 
      ogImage || 
      preloadedSEO?.og_image || 
      '',
    
    twitter_title: 
      twitterTitle || 
      ogTitle || 
      title || 
      preloadedSEO?.twitter_title || 
      preloadedSEO?.og_title || 
      preloadedSEO?.meta_title || 
      'Resumeslip',
    
    twitter_description: 
      twitterDescription || 
      ogDescription || 
      description || 
      preloadedSEO?.twitter_description || 
      preloadedSEO?.og_description || 
      preloadedSEO?.meta_description || 
      'Build professional resumes',
    
    twitter_image: 
      twitterImage || 
      ogImage || 
      preloadedSEO?.twitter_image || 
      preloadedSEO?.og_image || 
      '',
    
    canonical_url: 
      canonical || 
      preloadedSEO?.canonical_url || 
      window.location.href,
    
    robots_txt: 
      robots || 
      preloadedSEO?.robots_txt || 
      'index, follow',
  };

  const metaTags = seoService.generateMetaTags(seoData);

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{metaTags.title}</title>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      
      {/* SEO meta tags */}
      {metaTags.meta.map((tag, index) => {
        if (tag.name) {
          return <meta key={index} name={tag.name} content={tag.content} />;
        } else if (tag.property) {
          return <meta key={index} property={tag.property} content={tag.content} />;
        } else if (tag.rel) {
          return <link key={index} rel={tag.rel} href={tag.href} />;
        }
        return null;
      })}
      
      {/* Favicon from site config */}
      {siteConfig?.favicon && (
        <link rel="icon" href={siteConfig.favicon} />
      )}
      
      {/* Additional children meta tags */}
      {children}
    </Helmet>
  );
};

// Page-specific SEO components
export const HomeSEO = ({ pageData }) => (
  <SEO
    title={pageData?.meta_title}
    description={pageData?.meta_description}
    keywords={pageData?.meta_keywords}
    ogTitle={pageData?.og_title}
    ogDescription={pageData?.og_description}
    ogImage={pageData?.og_image}
    twitterTitle={pageData?.twitter_title}
    twitterDescription={pageData?.twitter_description}
    twitterImage={pageData?.twitter_image}
    canonical={pageData?.canonical_url}
    robots={pageData?.robots_txt}
  />
);

export const TemplateGallerySEO = ({ pageData }) => {
  const { data } = useData();
  const siteConfig = data.siteConfig;
  
  const seoData = {
    meta_title: pageData?.meta_title || 'Templates - Resumeslip',
    meta_description: pageData?.meta_description || pageData?.description || 'Browse our collection of professional resume, CV, and slip templates.',
    meta_keywords: pageData?.meta_keywords || 'resume templates, cv templates, professional templates, free templates',
    og_title: pageData?.og_title || pageData?.meta_title || 'Templates - Resumeslip',
    og_description: pageData?.og_description || pageData?.description || 'Browse our collection of professional resume, CV, and slip templates.',
    og_image: pageData?.og_image,
    twitter_title: pageData?.twitter_title || pageData?.og_title || pageData?.meta_title || 'Templates - Resumeslip',
    twitter_description: pageData?.twitter_description || pageData?.og_description || pageData?.description || 'Browse our collection of professional resume, CV, and slip templates.',
    twitter_image: pageData?.twitter_image || pageData?.og_image,
    canonical_url: pageData?.canonical_url || window.location.href,
    robots_txt: pageData?.robots_txt || 'index, follow',
  };

  const metaTags = seoService.generateMetaTags(seoData);

  return (
    <Helmet>
      <title>{metaTags.title}</title>
      {metaTags.meta.map((tag, index) => {
        if (tag.name) {
          return <meta key={index} name={tag.name} content={tag.content} />;
        } else if (tag.property) {
          return <meta key={index} property={tag.property} content={tag.content} />;
        } else if (tag.rel) {
          return <link key={index} rel={tag.rel} href={tag.href} />;
        }
        return null;
      })}
    </Helmet>
  );
};

// Individual Template SEO
export const TemplateSEO = ({ template }) => {
  const { data } = useData();
  const siteConfig = data.siteConfig;
  
  if (!template) {
    return <SEO title="Template - Resumeslip" description="Professional resume template" />;
  }

  const seoData = {
    meta_title: template.meta_title || `${template.name} - Resume Template`,
    meta_description:
      template.meta_description ||
      template.description ||
      `Download the ${template.name} resume template.`,
    meta_keywords:
      template.meta_keywords ||
      `resume template, ${template.name}, cv template, professional resume`,

    og_title:
      template.og_title ||
      template.meta_title ||
      `${template.name}`,
    og_description:
      template.og_description ||
      template.meta_description ||
      template.description ||
      `Download the ${template.name} resume template.`,
    og_image: template.og_image || template.thumbnail,

    twitter_title:
      template.twitter_title ||
      template.og_title ||
      template.meta_title ||
      `${template.name}`,
    twitter_description:
      template.twitter_description ||
      template.og_description ||
      template.meta_description ||
      template.description ||
      `Download the ${template.name} resume template.`,
    twitter_image:
      template.twitter_image ||
      template.og_image ||
      template.thumbnail,

    canonical_url: template.canonical_url || window.location.href,
    robots_txt: template.robots_txt || 'index, follow',
  };

  const metaTags = seoService.generateMetaTags(seoData);

  return (
    <Helmet>
      <title>{metaTags.title}</title>
      {metaTags.meta.map((tag, index) => {
        if (tag.name) {
          return <meta key={index} name={tag.name} content={tag.content} />;
        } else if (tag.property) {
          return <meta key={index} property={tag.property} content={tag.content} />;
        } else if (tag.rel) {
          return <link key={index} rel={tag.rel} href={tag.href} />;
        }
        return null;
      })}
      
      {/* Schema.org markup for template */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CreativeWork',
          name: template.name,
          description: template.description,
          image: template.thumbnail,
          author: {
            '@type': 'Organization',
            name: 'Resumeslip',
          },
          datePublished: template.created_at,
          dateModified: template.updated_at,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': window.location.href,
          },
        })}
      </script>
    </Helmet>
  );
};

export const AboutSEO = ({ pageData }) => (
  <SEO
    title={pageData?.meta_title || 'About Us - Resumeslip'}
    description={pageData?.meta_description || 'Learn about Resumeslip and our mission to help professionals create amazing resumes.'}
    ogTitle={pageData?.og_title}
    ogImage={pageData?.og_image}
  />
);

export const BlogSEO = ({ article }) => {
  if (!article) {
    return <SEO title="Blog - Resumeslip" description="Read our latest articles and career tips" />;
  }

  return (
    <SEO
      title={article.meta_title || article.title}
      description={article.meta_description || article.excerpt}
      keywords={article.meta_keywords}
      ogTitle={article.og_title || article.title}
      ogDescription={article.og_description || article.excerpt}
      ogImage={article.og_image || article.featured_image}
      twitterTitle={article.twitter_title || article.title}
      twitterDescription={article.twitter_description || article.excerpt}
      twitterImage={article.twitter_image || article.featured_image}
      canonical={article.canonical_url}
    >
      {/* Schema.org markup for blog posts */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: article.title,
          description: article.excerpt,
          image: article.featured_image,
          author: {
            '@type': 'Person',
            name: article.author,
            ...(article.author_role && { jobTitle: article.author_role }),
          },
          publisher: {
            '@type': 'Organization',
            name: 'Resumeslip',
            logo: {
              '@type': 'ImageObject',
              url: window.location.origin + '/logo.png',
            },
          },
          datePublished: article.publish_date,
          dateModified: article.updated_at,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': window.location.href,
          },
        })}
      </script>
    </SEO>
  );
};

export const ContactSEO = ({ pageData }) => (
  <SEO
    title={pageData?.meta_title || 'Contact Us - Resumeslip'}
    description={pageData?.meta_description || 'Get in touch with Resumeslip team for support, partnerships, or feedback.'}
  />
);

export default SEO;